import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";
import { useEmpresa } from "../../context/EmpresaContext";
import { useToast, ToastPopup } from "../../components/Toast";
import {
  getEventos, getRRPP, crearRRPP, editarRRPP, eliminarRRPP,
  getScanners, guardarScanner, eliminarScanner,
} from "../../services/sininApi";

const APP = window.location.origin;

export default function ClienteVendedores() {
  const navigate             = useNavigate();
  const location             = useLocation();
  const { empresa, colores } = useEmpresa();
  const GOLD                 = colores.primario;
  const FUENTE               = colores.fuente;
  const empresaId            = empresa?.empresaId;

  const eventoParam = new URLSearchParams(location.search).get("evento") || "";

  const [rrpp, setRrpp]           = useState([]);
  const [eventos, setEventos]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [newCred, setNewCred]     = useState(null);
  const [selected, setSelected]   = useState(null);
  const [eventoFiltro, setEventoFiltro] = useState(eventoParam);
  const { toast, showToast, closeToast } = useToast();

  const [form, setForm] = useState({
    first_name: "", last_name: "", dni: "", email: "",
    cel: "", password: "", accessRole: "rrpp", eventos: eventoParam ? [eventoParam] : [],
  });

  useEffect(() => {
    getEventos(empresaId, empresa?.loginType || "0")
      .then(data => setEventos(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [empresaId, empresa]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const cargar = async () => {
    setLoading(true);
    try {
      const [res, scanRes] = await Promise.all([
        getRRPP(empresaId),
        getScanners(empresaId),
      ]);
      const rrppItems = Array.isArray(res?.items) ? res.items.map(i => ({ ...i, _source: "rrpp" })) : [];
      const scannerItems = Array.isArray(scanRes?.items) ? scanRes.items.map(i => ({ ...i, _source: "users", accessRole: "scanner" })) : [];
      setRrpp([...rrppItems, ...scannerItems]);
    } catch {}
    setLoading(false);
  };

  // Con filtro de evento: separamos en "ya asignados a este evento" y "de la empresa, disponibles para sumar"
  const isScanner    = v => v.accessRole === "scanner" || v._source === "users";
  const tieneEvento  = v => !isScanner(v) && Array.isArray(v.eventos) && v.eventos.includes(eventoFiltro);
  const asignados    = eventoFiltro ? rrpp.filter(tieneEvento) : rrpp;
  const disponibles  = eventoFiltro ? rrpp.filter(v => !isScanner(v) && !tieneEvento(v)) : [];

  const handleAsignar = async (v, asignar) => {
    const actuales = Array.isArray(v.eventos) ? v.eventos : [];
    const eventos  = asignar
      ? [...new Set([...actuales, eventoFiltro])]
      : actuales.filter(id => id !== eventoFiltro);
    try {
      await editarRRPP(empresaId, { ...v, eventos });
      cargar();
    } catch { showToast("Error al actualizar asignación.", "error"); }
  };

  const handleEliminar = async (v) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${v.first_name} ${v.last_name}? No se puede deshacer.`)) return;
    try {
      if (isScanner(v)) await eliminarScanner(empresaId, v.dni);
      else await eliminarRRPP(empresaId, v.dni);
      setSelected(null);
      cargar();
      showToast("Eliminado correctamente.", "success");
    } catch { showToast("Error al eliminar.", "error"); }
  };

  const setF = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleCrear = async () => {
    if (!form.first_name || !form.last_name || !form.dni)
      return showToast("Nombre, apellido y DNI requeridos.", "error");
    try {
      const pass = form.password || Math.random().toString(36).slice(-8);
      const payload = { ...form, password: pass };
      const resultado = form.accessRole === "scanner"
        ? await guardarScanner(empresaId, {
            ...payload,
            loginType: "2",
            accessRole: "scanner",
            active: "1",
          })
        : await crearRRPP(empresaId, {
            ...payload,
            loginType: "2",
            accessRole: "rrpp",
            eventos: form.eventos,
          });
      setNewCred({
        nombre: `${form.first_name} ${form.last_name}`,
        dni: form.dni,
        password: resultado?.usesExistingAccount ? null : pass,
        usesExistingAccount: !!resultado?.usesExistingAccount,
        accessRole: form.accessRole,
        idEvento: form.accessRole === "scanner" ? "" : (form.eventos[0] || ""),
      });
      setForm({ first_name: "", last_name: "", dni: "", email: "", cel: "", password: "", accessRole: "rrpp", eventos: eventoFiltro ? [eventoFiltro] : [] });
      setShowForm(false);
      cargar();
    } catch { showToast("Error al crear RRPP.", "error"); }
  };

  const handleToggle = async (v) => {
    try {
      const updated = { ...v, active: v.active === "1" ? "0" : "1" };
      if (isScanner(v)) await guardarScanner(empresaId, updated);
      else await editarRRPP(empresaId, updated);
      cargar();
    } catch { showToast("Error al actualizar.", "error"); }
  };


  const styles = makeStyles(GOLD, FUENTE);

  return (
    <div style={styles.page}>
      <div style={{ ...styles.header, background: empresa?.colorFondo || "#1a1a1a", color: FUENTE }}>
        <button style={styles.backBtn} onClick={() => navigate("/cliente/eventos")}>← Eventos</button>
        <span style={{ fontWeight: "700", display: "flex", alignItems: "center", gap: 8 }}>
          {empresa?.logoUrl && <img src={empresa.logoUrl} alt="" style={{ width: 38, height: 38, objectFit: "contain" }} />}
          RRPP
        </span>
        <button style={styles.outlineBtn} onClick={() => setShowForm(p => !p)}>
          {showForm ? "Cancelar" : "+ Nuevo usuario"}
        </button>
      </div>

      <div style={styles.content}>
        <select style={styles.select} value={eventoFiltro} onChange={e => setEventoFiltro(e.target.value)}>
          <option value="">Todos los eventos</option>
          {eventos.map(ev => <option key={ev.idEvento} value={ev.idEvento}>{ev.name || ev.nombre}</option>)}
        </select>

        {showForm && (
          <div style={styles.card}>
            <h3 style={{ marginTop: 0 }}>Nuevo usuario</h3>
            <label style={styles.label}>Tipo de acceso</label>
            <select style={styles.select} value={form.accessRole} onChange={setF("accessRole")}>
              <option value="rrpp">RRPP / Vendedor</option>
              <option value="scanner">Scanner / Puerta</option>
            </select>
            {[["first_name","Nombre *","Juan"],["last_name","Apellido *","Pérez"],
              ["dni","DNI *","12345678"],["email","Email","rrpp@email.com"],
              ["cel","Celular","1112345678"]].map(([k, lbl, ph]) => (
              <React.Fragment key={k}>
                <label style={styles.label}>{lbl}</label>
                <input style={styles.input} value={form[k]} onChange={setF(k)} placeholder={ph} />
              </React.Fragment>
            ))}
            <label style={styles.label}>Contraseña (vacío = automática)</label>
            <input style={styles.input} value={form.password} onChange={setF("password")} placeholder="Dejar vacío" />
            {form.accessRole === "rrpp" ? (
              <>
                <label style={styles.label}>Eventos asignados</label>
                {eventos.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#aaa" }}>No hay eventos creados todavía.</div>
                ) : eventos.map(ev => (
                  <label key={ev.idEvento} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer", fontSize: 14 }}>
                    <input type="checkbox" checked={form.eventos.includes(ev.idEvento)}
                      onChange={e => setForm(p => ({
                        ...p,
                        eventos: e.target.checked
                          ? [...p.eventos, ev.idEvento]
                          : p.eventos.filter(id => id !== ev.idEvento),
                      }))} />
                    {ev.name || ev.nombre}
                  </label>
                ))}
              </>
            ) : (
              <div style={{ background: "#f4f4f4", borderRadius: 10, padding: 10, marginTop: 12, fontSize: 13, color: "#555" }}>
                Este usuario entra por el login general y solo abre el scanner de {empresaId}. No ve invitados, ventas ni estadísticas.
              </div>
            )}
            <button style={{ ...styles.goldBtn, width: "100%", marginTop: 16 }} onClick={handleCrear}>
              Crear {form.accessRole === "scanner" ? "scanner" : "RRPP"}
            </button>
          </div>
        )}

        {/* Popup credenciales */}
        {newCred && (
          <div style={styles.overlay}>
            <div style={styles.popup}>
              <h3 style={{ marginTop: 0 }}>✅ Usuario creado</h3>
              <p style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
                Guardá estas credenciales — la contraseña no se vuelve a mostrar.
              </p>
              <div style={styles.credRow}><b>Empresa:</b> {empresaId}</div>
              <div style={styles.credRow}><b>Acceso:</b> {newCred.accessRole === "scanner" ? "Scanner / Puerta" : "RRPP / Vendedor"}</div>
              <div style={styles.credRow}><b>Nombre:</b> {newCred.nombre}</div>
              <div style={styles.credRow}><b>DNI:</b> {newCred.dni}</div>
              <div style={styles.credRow}>
                <b>Contraseña:</b>{" "}
                {newCred.usesExistingAccount ? (
                  <span>Conserva su contraseña global actual.</span>
                ) : (
                  <code style={{ background: "#f0f0f0", padding: "2px 6px", borderRadius: 4 }}>
                    {newCred.password}
                  </code>
                )}
              </div>
              {newCred.idEvento && (
                <div style={styles.credRow}>
                  <b>Link:</b>{" "}
                  <code style={{ fontSize: 11, wordBreak: "break-all" }}>
                    {APP}/i/{empresaId}/{newCred.idEvento}/{newCred.dni}
                  </code>
                </div>
              )}
              <button style={{ ...styles.goldBtn, width: "100%", marginTop: 16 }} onClick={() => setNewCred(null)}>
                Entendido
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <GlassSpinner size={70} background={empresa?.colorFondo || "#1a1a1a"} />
          </div>
        ) : rrpp.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888", padding: 30 }}>No hay RRPP todavía.</p>
        ) : (
          <>
            {asignados.length === 0 ? (
              <p style={{ textAlign: "center", color: "#888", padding: 20, fontSize: 13 }}>
                Ningún RRPP asignado a este evento todavía.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {asignados.map(v => {
                  const nombresEventos = (v.eventos || [])
                    .map(id => eventos.find(e => e.idEvento === id)?.name)
                    .filter(Boolean);
                  return (
                    <div key={v.dni}
                      style={{ ...styles.card, opacity: v.active === "1" ? 1 : 0.55, display: "flex",
                        justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      {eventoFiltro && (
                        <input type="checkbox" checked={true}
                          title="Desasignar de este evento"
                          onClick={e => e.stopPropagation()}
                          onChange={() => handleAsignar(v, false)}
                          style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setSelected(v)}>
                        <div style={{ fontWeight: "700" }}>{v.first_name} {v.last_name}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>
                          DNI {v.dni} {v.cel ? `· ${v.cel}` : ""}
                        </div>
                        {isScanner(v) && (
                          <div style={{ fontSize: 12, color: "#198754", marginTop: 2, fontWeight: "700" }}>🔍 SCANNER / PUERTA</div>
                        )}
                        {!eventoFiltro && nombresEventos.length > 0 && (
                          <div style={{ fontSize: 12, color: GOLD, marginTop: 2 }}>📅 {nombresEventos.join(", ")}</div>
                        )}
                      </div>
                      <span style={{ fontSize: 18, color: "#ccc", cursor: "pointer" }} onClick={() => setSelected(v)}>›</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* RRPP de la empresa no asignados a este evento — disponibles para sumar */}
            {eventoFiltro && disponibles.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: "700", color: "#888", textTransform: "uppercase",
                  letterSpacing: 1, marginTop: 20, marginBottom: 8 }}>
                  RRPP de la empresa — disponibles para este evento
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {disponibles.map(v => (
                    <div key={v.dni} style={{ ...styles.card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <input type="checkbox" checked={false}
                        title="Asignar a este evento"
                        onChange={() => handleAsignar(v, true)}
                        style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
                      <div style={{ cursor: "pointer", flex: 1 }} onClick={() => setSelected(v)}>
                        <div style={{ fontWeight: "700" }}>{v.first_name} {v.last_name}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>DNI {v.dni}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Card detalle RRPP ── */}
        {selected && (() => {
          const eventosSel = Array.isArray(selected.eventos) ? selected.eventos : [];
          const toggleEventoSel = (idEvento) => {
            const nuevos = eventosSel.includes(idEvento)
              ? eventosSel.filter(id => id !== idEvento)
              : [...eventosSel, idEvento];
            editarRRPP(empresaId, { ...selected, eventos: nuevos }).then(() => {
              setSelected(p => ({ ...p, eventos: nuevos }));
              cargar();
            }).catch(() => showToast("Error al actualizar eventos.", "error"));
          };
          return (
            <div style={styles.overlay} onClick={() => setSelected(null)}>
              <div style={styles.detailCard} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: "800", fontSize: 18 }}>{selected.first_name} {selected.last_name}</div>
                    <div style={{ fontSize: 13, color: "#888" }}>DNI {selected.dni}</div>
                  </div>
                  <button onClick={() => setSelected(null)}
                    style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>✕</button>
                </div>

                {/* Datos */}
                <div style={styles.detailRow}><span style={styles.detailLabel}>Email</span><span>{selected.email || "—"}</span></div>
                <div style={styles.detailRow}><span style={styles.detailLabel}>Celular</span><span>{selected.cel || "—"}</span></div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Tipo</span>
                  <span style={{ fontWeight: "700" }}>
                    {isScanner(selected) ? "Scanner / Puerta" : "RRPP / Vendedor"}
                  </span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Estado</span>
                  <span style={{ fontWeight: "700", color: selected.active === "1" ? "#198754" : "#dc3545" }}>
                    {selected.active === "1" ? "Activo" : "Inactivo"}
                  </span>
                </div>

                {/* Eventos asignados */}
                {!isScanner(selected) ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={styles.sectionTitle}>Eventos asignados</div>
                    {eventos.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#aaa" }}>No hay eventos creados.</div>
                    ) : eventos.map(ev => (
                      <label key={ev.idEvento} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer", fontSize: 14 }}>
                        <input type="checkbox" checked={eventosSel.includes(ev.idEvento)}
                          onChange={() => toggleEventoSel(ev.idEvento)} />
                        {ev.name}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 14, background: "#eaf7ef", color: "#0f5132", borderRadius: 10, padding: 10, fontSize: 13 }}>
                    Este usuario solo puede escanear tickets de {empresaId}. No tiene eventos asignables.
                  </div>
                )}

                {/* Credenciales */}
                <div style={{ marginTop: 14 }}>
                  <div style={styles.sectionTitle}>Credenciales de acceso</div>
                  <div style={{ background: "#f4f4f4", borderRadius: 8, padding: "10px 12px", fontSize: 13,
                    color: "#333", marginTop: 4, lineHeight: 1.8 }}>
                    <div><b>Empresa:</b> {empresa?.empresaId}</div>
                    <div><b>Acceso:</b> {isScanner(selected) ? "Scanner / Puerta" : "RRPP / Vendedor"}</div>
                    <div><b>DNI:</b> {selected.dni}</div>
                    <div><b>Contraseña:</b> {selected._passVis || "••••••••"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button style={{ ...styles.goldBtn, flex: 1 }}
                      onClick={() => {
                        const txt = `Empresa: ${empresa?.empresaId}\nAcceso: ${isScanner(selected) ? "Scanner / Puerta" : "RRPP / Vendedor"}\nLogin: ${APP}/plataforma/login\nDNI: ${selected.dni}\nContraseña: ${selected._passVis || "(resetear para ver)"}`;
                        navigator.clipboard.writeText(txt);
                        showToast("¡Credenciales copiadas!", "success");
                      }}>
                      Copiar
                    </button>
                    <button style={{ ...styles.grayBtn }}
                      onClick={async () => {
                        const newPass = Math.random().toString(36).slice(-8);
                        try {
                          if (isScanner(selected)) await guardarScanner(empresaId, { ...selected, password: newPass });
                          else await editarRRPP(empresaId, { ...selected, password: newPass });
                          setSelected(p => ({ ...p, _passVis: newPass }));
                        } catch { showToast("Error al resetear.", "error"); }
                      }}>
                      Resetear clave
                    </button>
                  </div>
                </div>

                {/* Acciones */}
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button style={{ flex: 1, ...styles.goldBtn,
                    background: selected.active === "1" ? "#dc3545" : "#198754", color: "#fff" }}
                    onClick={() => { handleToggle(selected); setSelected(null); }}>
                    {selected.active === "1" ? "Inactivar" : "Activar"}
                  </button>
                  <button style={{ ...styles.grayBtn }} onClick={() => setSelected(null)}>
                    Cerrar
                  </button>
                </div>
                <button style={{ ...styles.grayBtn, width: "100%", marginTop: 8,
                  background: "#f8d7da", color: "#842029", border: "none" }}
                  onClick={() => handleEliminar(selected)}>
                  Eliminar definitivamente
                </button>
              </div>
            </div>
          );
        })()}
      </div>
      <ToastPopup toast={toast} onClose={closeToast} accentColor={GOLD} accentText={FUENTE} />
    </div>
  );
}

const makeStyles = (GOLD, FUENTE) => ({
  page:       { background: "#f4f4f4", minHeight: "100vh" },
  header:     { background: "#1a1a1a", color: "#fff", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  content:    { padding: 16, maxWidth: 700, margin: "0 auto" },
  card:       { background: "#fff", borderRadius: 14, padding: 16, marginBottom: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  label:      { display: "block", fontWeight: "600", fontSize: 13, marginTop: 10, marginBottom: 4, color: "#444" },
  input:      { padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box", width: "100%" },
  select:     { padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, width: "100%", marginBottom: 14, background: "#fff" },
  goldBtn:    { background: GOLD, color: FUENTE, border: "none", borderRadius: 25, padding: "8px 16px", cursor: "pointer", fontWeight: "700", fontSize: 13 },
  grayBtn:    { background: "transparent", color: "#555", border: "1px solid #ccc", borderRadius: 8, padding: "7px 13px", cursor: "pointer", fontSize: 12 },
  outlineBtn: { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 },
  backBtn:    { background: "transparent", color: GOLD, border: "none", fontSize: 14, cursor: "pointer", fontWeight: "600" },
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  popup:       { background: "#fff", borderRadius: 16, padding: 24, width: 320, boxShadow: "0 6px 20px rgba(0,0,0,0.3)" },
  credRow:     { fontSize: 14, marginBottom: 8, color: "#333" },
  detailCard:  { background: "#fff", borderRadius: 20, padding: 24, width: "100%", maxWidth: 420, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.25)" },
  detailRow:   { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 },
  detailLabel: { color: "#888", fontWeight: "600" },
  sectionTitle:{ fontSize: 11, fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4, marginTop: 2 },
});
